import {Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {tapResponse} from '@ngrx/operators';
import {Store} from '@ngrx/store';
import {Bundle, Image} from '@sportngin/se-commerce-sdk/models/clare';
import {EMPTY, exhaustMap, forkJoin, map, Observable, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import {defaultProductQueryParams, ProductQueryParams, SpecsAndVariants} from '../../../../models';
import {
  clareProductToProduct,
  CatalogSummary,
  Store as ClareStore,
  getProductDetailDisplayPrice,
  getProductId,
  Product,
} from '../../../../models/clare';
import {
  BundleProductsUpdateProduct,
  BundleProductsUpdateRequest,
  UpdateBundleRequest,
} from '../../../../models/clare/bundle';
import {BundleApiService} from '../../../../services/bundle/bundle-api.service';
import {AnalyticsEventProperties, AnalyticsEvents, AnalyticsService} from '../../../../services/analytics.service';
import {ProductApiService} from '../../../../services/product/product-api.service';
import {StoreApiService} from '../../../../services/store-api.service';
import {CatalogApiService} from '../../../../services/catalog-api.service';
import {
  selectHistory,
  selectQueryParams,
  selectSelectedOrganizationId,
  selectSelectedProductId,
} from '../../../../state/router/router.selectors';
import {
  ProductOrganizationUpdate,
  ProductImagesUpdate,
  UpdateProductDetail,
} from '../../../product/components/product-detail/product-detail.store';
import {CatalogMembershipSyncService} from '../../../../services/catalog-membership-sync.service';
import {canBundleBeActive, hasActiveVariations} from '../../utils/bundle-activation.utils';
import {bundleResponseToItem, itemToUpdateBundleRequest} from '../../utils/bundle-mappers';

/** State shape for the bundle detail component store. */
export interface BundleDetailState {
  item: Product | undefined;
  bundle: Bundle | undefined;
  priceSchedule: undefined;
  loadingArray: string[];
  store: ClareStore | undefined;
  error: string | null;
  statusUpdating?: boolean;
  bundleProducts: Product[];
  catalogs: CatalogSummary[];
  catalogsLoading: boolean;
  organizationCatalogs: CatalogSummary[];
  organizationCatalogsLoading: boolean;
}

/** View model for the bundle detail template. */
export interface BundleDetailVm {
  product: Product | undefined;
  bundle: Bundle | undefined;
  loading: boolean;
  image: {src: string; alt: string};
  images: Image[];
  vendorName: string;
  error: string | null;
  backLink: {backLink: string; backLinkParams: Record<string, string>};
  syncedWithClare: boolean;
  price: number;
  statusUpdating: boolean | undefined;
  brandName: string | undefined;
  canActivateBundle: boolean;
  ineligibleProducts: Product[];
  catalogs: CatalogSummary[];
  catalogsLoading: boolean;
  organizationCatalogs: CatalogSummary[];
  organizationCatalogsLoading: boolean;
  organizationId: string;
}

/** Component store contract for bundle detail (shared UI with product detail). */
export interface BundleDetailComponentStore {
  vm$: Observable<BundleDetailVm>;
  error$: Observable<string | null>;
  loadBundle: () => void;
  loadOrganizationCatalogs: () => void;
  updateProductStatus: (active: boolean) => void;
  updateVariantStatus: (event: {id: string; active: boolean}) => void;
  updateSpecsAndVariants: (event: UpdateProductDetail<SpecsAndVariants>) => void;
  updateProductImageAndDescription: (event: UpdateProductDetail<ProductImagesUpdate>) => void;
  saveBundleCatalogs: (event: UpdateProductDetail<ProductOrganizationUpdate>) => void;
  updateBundle: (event: UpdateProductDetail<{bundle: Partial<Bundle>}>) => void;
  setError: (error: string | null) => void;
  addProductsToBundle: (products: BundleProductsUpdateProduct[]) => void;
  removeProductsFromBundle: (products: BundleProductsUpdateProduct[]) => void;
  updateBundleProducts: (params: {
    productsToAdd: BundleProductsUpdateProduct[];
    productsToRemove: BundleProductsUpdateProduct[];
    price?: number;
  }) => void;
  bundleProducts$: Observable<Product[]>;
  setAllProductsToActive: () => void;
}

const defaultImage = {
  src: '/assets/images/no-image-available.svg',
  alt: 'No Image Available',
};

/**
 * Component store for bundle detail. Uses organization-nested Bundle routes so the selected route context determines
 * which bundle is loaded and updated.
 */
@Injectable()
export class BundleDetailStore extends ComponentStore<BundleDetailState> implements BundleDetailComponentStore {
  /**
   * @deprecated use `bundle$` instead
   * @type {Observable<Product | undefined>}
   */
  readonly item$ = this.select(state => state.item);
  readonly bundle$ = this.select(state => state.bundle);
  /**
   * @deprecated use `bundle$` instead
   * @type {Observable<unknown>}
   */
  readonly product$ = this.select(this.item$, item => {
    if (!item) {
      return undefined;
    }
    return clareProductToProduct(item);
  });
  readonly loading$ = this.select(state => state.loadingArray.length > 0);
  readonly image$ = this.select(this.item$, item => {
    if (item?.display_metadata?.images?.length) {
      return item.display_metadata.images[0];
    }
    return defaultImage;
  });
  readonly images$ = this.select(this.item$, item => {
    if (item?.display_metadata?.images?.length) {
      return item.display_metadata.images;
    }
    return [defaultImage];
  });
  readonly vendorName$ = this.select(state => state.store?.name ?? '');
  readonly error$ = this.select(state => state.error);
  readonly itemUpdating$ = this.select(state => state.statusUpdating ?? false);
  readonly bundleProducts$ = this.select(state => state.bundleProducts);
  readonly catalogs$ = this.select(state => state.catalogs);
  readonly catalogsLoading$ = this.select(state => state.catalogsLoading);
  readonly organizationCatalogs$ = this.select(state => state.organizationCatalogs);
  readonly organizationCatalogsLoading$ = this.select(state => state.organizationCatalogsLoading);
  readonly organizationId$ = this.store
    .select(selectSelectedOrganizationId)
    .pipe(map(organizationId => organizationId ?? ''));
  readonly brandName$ = this.select(this.bundleProducts$, products => {
    const brandNames = products.map(p => p.display_metadata?.brand ?? '').filter(Boolean);
    const seen = new Map<string, string>();
    brandNames.forEach(brand => {
      const lower = brand.toLowerCase();
      if (!seen.has(lower)) {
        seen.set(lower, brand);
      }
    });
    const brands = [...seen.values()];
    return brands.join(', ');
  });
  readonly canActivateBundle$ = this.select(this.item$, this.bundleProducts$, (item, bundleProducts) =>
    canBundleBeActive(item?.name, bundleProducts)
  );

  readonly backLink$ = this.select(
    this.store.select(selectHistory).pipe(
      map(history => {
        const isProductOrBundleDetail = (u: string) =>
          /\/products\/[^/?]+(?:\?.*)?$/.test(u) || /\/bundles\/(?!new(?:\?|$))[^/?]+(?:\?.*)?$/.test(u);
        const isCreateBundlePage = (u: string) => /\/bundles\/new\/?(?:\?|$)/.test(u);
        for (let i = history.length - 1; i >= 0; i--) {
          const url = history[i];
          if (url && !isProductOrBundleDetail(url) && !isCreateBundlePage(url)) {
            return decodeURIComponent(url);
          }
        }
        return undefined;
      }),
      withLatestFrom(this.store.select(selectSelectedOrganizationId), this.store.select(selectQueryParams))
    ),
    ([prevUrl, orgId, currentQueryParams]) => {
      const baseLink = `/org/${orgId}/commerce/products`;
      const fallbackParams = this.extractQueryParams(currentQueryParams);
      if (!prevUrl || prevUrl === 'undefined') {
        return {backLink: baseLink, backLinkParams: fallbackParams};
      }
      try {
        const url = prevUrl.startsWith('http') ? new URL(prevUrl) : new URL(prevUrl, window.location.origin);
        const backLink = url.pathname;
        const allowedKeys = ['page', 'take', 'query', 'sortBy', 'direction'];
        const backLinkParams: Record<string, string> = {};
        url.searchParams.forEach((value, key) => {
          if (allowedKeys.includes(key)) {
            backLinkParams[key] = String(value);
          }
        });
        return {
          backLink,
          backLinkParams: Object.keys(backLinkParams).length ? backLinkParams : fallbackParams,
        };
      } catch {
        return {backLink: baseLink, backLinkParams: fallbackParams};
      }
    }
  );

  readonly syncedWithClare$ = this.select(() => true);
  readonly price$ = this.select(state => {
    const item = state.item;
    let price = 0;

    if (item) {
      const embeddedDefault = item.default_variation;
      if (embeddedDefault) {
        if (embeddedDefault.price_cents !== null && embeddedDefault.price_cents !== undefined) {
          price = embeddedDefault.price_cents / 100;
        } else if (embeddedDefault.price !== null && embeddedDefault.price !== undefined) {
          price = Number(embeddedDefault.price);
        }
      } else {
        const defaultVariationId = item.default_variation_id;
        const defaultVariation = item.variations?.find(v => v.id === defaultVariationId);
        if (defaultVariation) {
          if (defaultVariation.price_cents !== null && defaultVariation.price_cents !== undefined) {
            price = defaultVariation.price_cents / 100;
          } else if (defaultVariation.price !== null && defaultVariation.price !== undefined) {
            price = Number(defaultVariation.price);
          }
        }
      }
    }

    return price;
  });
  readonly ineligibleProducts$ = this.select(
    state =>
      state.bundle?.products
        .filter(p => !this.isDiscardedIncludedProduct(p))
        .filter(p => !hasActiveVariations(p))
        .map(product => ({
          ...product,
          only_sold_in_bundles: (product as Product).only_sold_in_bundles ?? false,
        })) ?? []
  );

  readonly vm$ = this.select(
    this.product$,
    this.loading$,
    this.image$,
    this.images$,
    this.vendorName$,
    this.error$,
    this.backLink$,
    this.syncedWithClare$,
    this.price$,
    this.itemUpdating$,
    this.brandName$,
    this.canActivateBundle$,
    this.bundle$,
    this.ineligibleProducts$,
    this.catalogs$,
    this.catalogsLoading$,
    this.organizationCatalogs$,
    this.organizationCatalogsLoading$,
    this.organizationId$,
    (
      product,
      loading,
      image,
      images,
      vendorName,
      error,
      backLink,
      syncedWithClare,
      price,
      itemUpdating,
      brandName,
      canActivateBundle,
      bundle,
      ineligibleProducts,
      catalogs,
      catalogsLoading,
      organizationCatalogs,
      organizationCatalogsLoading,
      organizationId
    ): BundleDetailVm => ({
      product,
      loading,
      image,
      images,
      vendorName,
      error,
      backLink,
      syncedWithClare,
      price,
      statusUpdating: itemUpdating,
      brandName,
      canActivateBundle,
      bundle,
      ineligibleProducts,
      catalogs,
      catalogsLoading,
      organizationCatalogs,
      organizationCatalogsLoading,
      organizationId,
    })
  );

  readonly addLoading = this.updater((state, loadingThing: string) => ({
    ...state,
    loadingArray: [...state.loadingArray, loadingThing],
  }));
  readonly removeLoading = this.updater((state, loadingThing: string) => ({
    ...state,
    loadingArray: state.loadingArray.filter(l => l !== loadingThing),
  }));
  readonly setError = this.updater((state, error: string | null) => ({...state, error}));

  /** Loads the bundle by id for the shared detail UI. */
  readonly loadBundle = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => this.addLoading('bundle')),
      withLatestFrom(this.store.select(selectSelectedProductId), this.store.select(selectSelectedOrganizationId)),
      switchMap(([, bundleId, organizationId]) =>
        this.bundleApi.getBundle(Number(bundleId!), organizationId!).pipe(
          switchMap((bundle: Bundle) => {
            const preliminary = this.mapIncludedProductsToProducts(bundle);
            return this.enrichIncludedProductsWithMergedPrices(bundle, preliminary).pipe(
              map(bundleProducts => ({bundle, bundleProducts}))
            );
          }),
          tapResponse({
            next: ({bundle, bundleProducts}) => {
              this.patchState({item: bundleResponseToItem(bundle), bundle, bundleProducts});
              this.loadBundleCatalogs();
              this.loadStore();
            },
            error: () => this.setError('An error occurred. Please refresh and try again'),
            finalize: () => this.removeLoading('bundle'),
          })
        )
      )
    )
  );

  readonly loadBundleCatalogs = this.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.store.select(selectSelectedProductId), this.store.select(selectSelectedOrganizationId)),
      tap(() => this.patchState({catalogs: [], catalogsLoading: true})),
      switchMap(([, bundleId, organizationId]) =>
        this.catalogApi.getAllCatalogsByOrganizationId(organizationId!, {bundle_id: bundleId!}).pipe(
          tapResponse({
            next: catalogs => this.patchState({catalogs}),
            error: () => this.patchState({catalogs: []}),
            finalize: () => this.patchState({catalogsLoading: false}),
          })
        )
      )
    )
  );

  readonly loadOrganizationCatalogs = this.effect<void>(trigger$ =>
    trigger$.pipe(
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      tap(() => this.patchState({organizationCatalogs: [], organizationCatalogsLoading: true})),
      switchMap(([, organizationId]) =>
        this.catalogApi.getAllCatalogsByOrganizationId(organizationId!).pipe(
          tapResponse({
            next: organizationCatalogs => this.patchState({organizationCatalogs}),
            error: () => this.patchState({organizationCatalogs: []}),
            finalize: () => this.patchState({organizationCatalogsLoading: false}),
          })
        )
      )
    )
  );

  /** Bundle detail uses PUT (full replace) through the organization-nested Bundle route. */
  readonly updateProductStatus = this.effect((status$: Observable<boolean>) =>
    status$.pipe(
      withLatestFrom(this.item$, this.bundleProducts$, this.bundle$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([active, item, bundleProducts, bundle, organizationId]) => {
        if (item?.id === undefined || item?.id === null) {
          return EMPTY;
        }
        if (active && !canBundleBeActive(item.name, bundleProducts)) {
          return EMPTY;
        }
        const bundleId = item.id;
        this.patchState({statusUpdating: true});
        const body = itemToUpdateBundleRequest(item, {active});
        const cleanup$ = active ? this.removeDiscardedProductsFromBundle(bundleId, bundle, organizationId!) : of(null);
        return cleanup$.pipe(
          switchMap(() => this.bundleApi.updateBundle(bundleId, body, organizationId!)),
          tapResponse({
            next: updated => {
              this.patchState({item: bundleResponseToItem(updated)});
              this.toast.done('Bundle updated');
              this.trackBundleStatusChange(item.active, updated);
            },
            error: () => this.setError('An error occurred. Please refresh and try again'),
            finalize: () => this.patchState({statusUpdating: false}),
          })
        );
      })
    )
  );

  readonly updateVariantStatus = this.effect((_status$: Observable<{id: string; active: boolean}>) => _status$);

  readonly updateSpecsAndVariants = this.effect((event$: Observable<UpdateProductDetail<SpecsAndVariants>>) =>
    event$.pipe(
      tap(event => {
        event.modal?.close();
      })
    )
  );

  readonly updateProductImageAndDescription = this.effect(
    (event$: Observable<UpdateProductDetail<ProductImagesUpdate>>) =>
      event$.pipe(
        withLatestFrom(this.item$, this.store.select(selectSelectedOrganizationId)),
        exhaustMap(([event, item, organizationId]) => {
          if (!item || item.id === null || item.id === undefined) {
            event.modal?.close();
            return EMPTY;
          }
          const mergedItem = {
            ...item,
            display_metadata: {
              ...item.display_metadata,
              images: event.data.images ?? [],
            },
          };
          const hasNewBundleMedia = this.hasNewBundleMedia(item.display_metadata?.images, event.data.images ?? []);
          const body = itemToUpdateBundleRequest(mergedItem);
          return this.bundleApi.updateBundle(item.id, body, organizationId!).pipe(
            tapResponse({
              next: updated => {
                this.patchState({item: bundleResponseToItem(updated)});
                this.toast.done('Bundle updated');
                if (hasNewBundleMedia) {
                  this.trackBundleMedia(updated.id);
                }
                this.setError(null);
                event.modal?.close();
              },
              error: () => this.setError('An error occurred. Please refresh and try again'),
              finalize: () => {
                event.modal?.cancelSpinner();
              },
            })
          );
        })
      )
  );

  readonly saveBundleCatalogs = this.effect((event$: Observable<UpdateProductDetail<ProductOrganizationUpdate>>) =>
    event$.pipe(
      withLatestFrom(this.bundle$, this.catalogs$, this.organizationCatalogs$),
      exhaustMap(([{data, modal}, bundle, catalogs, organizationCatalogs]) => {
        if (!bundle || bundle.id === undefined || bundle.id === null) {
          modal?.cancelSpinner();
          this.setError('An error occurred. Please refresh and try again');
          return EMPTY;
        }

        return this.updateBundleCatalogs(bundle.id, data.catalogIds, catalogs, organizationCatalogs).pipe(
          tapResponse({
            next: catalogUpdate => {
              if (catalogUpdate.changed) {
                this.patchState({catalogs: catalogUpdate.catalogs});
                this.toast.done('Bundle updated');
              }
              this.setError(null);
              modal?.close();
            },
            error: () => this.setError('An error occurred. Please refresh and try again'),
            finalize: () => modal?.cancelSpinner(),
          })
        );
      })
    )
  );

  readonly updateBundle = this.effect((event$: Observable<UpdateProductDetail<{bundle: Partial<Bundle>}>>) =>
    event$.pipe(
      withLatestFrom(this.bundle$, this.bundleProducts$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([event, bundle, previousBundleProducts, organizationId]) => {
        if (!bundle || bundle.id === null || bundle.id === undefined) {
          event.modal?.close();
          return EMPTY;
        }

        const updateRequest: UpdateBundleRequest = {
          name: event.data.bundle.name ? String(event.data.bundle.name) : undefined,
          description: event.data.bundle.description ? String(event.data.bundle.description) : undefined,
          price_cents:
            event.data.bundle.price_cents !== null && event.data.bundle.price_cents !== undefined
              ? event.data.bundle.price_cents
              : undefined,
        };

        return this.bundleApi.updateBundle(bundle.id, updateRequest, organizationId!).pipe(
          switchMap(updated =>
            this.enrichIncludedProductsWithMergedPrices(
              updated,
              this.mapIncludedProductsToProducts(updated, previousBundleProducts)
            ).pipe(map(bundleProducts => ({updated, bundleProducts})))
          ),
          tapResponse({
            next: ({updated, bundleProducts}) => {
              this.patchState({bundle: updated, item: bundleResponseToItem(updated), bundleProducts});
              this.toast.done('Bundle updated successfully');
              this.setError(null);
              event.modal?.close();
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              event.modal?.cancelSpinner();
            },
          })
        );
      })
    )
  );

  /**
   * Adds products to the bundle using the Bundle API.
   *
   * This wraps the organization-nested Bundle add-products route and shows a toast on success.
   */
  readonly addProductsToBundle = this.effect((products$: Observable<BundleProductsUpdateProduct[]>) => {
    return products$.pipe(
      withLatestFrom(this.bundle$, this.bundleProducts$, this.store.select(selectSelectedOrganizationId)),
      exhaustMap(([products, bundle, previousBundleProducts, organizationId]) => {
        let request$ = EMPTY as Observable<never>;
        const hasValidItemId = bundle?.id !== undefined && bundle?.id !== null;
        const hasProducts = products.length > 0;
        if (hasValidItemId && hasProducts) {
          this.addLoading('bundleProducts');
          const body: BundleProductsUpdateRequest = {
            products,
          };
          request$ = this.bundleApi.addProductsToBundle(bundle.id as number, body, organizationId!).pipe(
            switchMap(updated =>
              this.enrichIncludedProductsWithMergedPrices(
                updated,
                this.mapIncludedProductsToProducts(updated, previousBundleProducts)
              ).pipe(
                switchMap(bundleProducts => {
                  this.patchState({bundle: updated, item: bundleResponseToItem(updated), bundleProducts});
                  this.toast.done('Products added to bundle');
                  this.trackBundleProductChange(AnalyticsEvents.AddBundleProduct, updated.id, products);
                  return this.deactivateBundleIfIneligible(updated, bundleProducts, organizationId!);
                })
              )
            ),
            tapResponse({
              next: updated => {
                if (updated) {
                  this.patchState({bundle: updated, item: bundleResponseToItem(updated)});
                }
              },
              error: () => {
                this.setError('An error occurred. Please refresh and try again');
              },
              finalize: () => {
                this.removeLoading('bundleProducts');
              },
            })
          ) as unknown as Observable<never>;
        }
        return request$;
      })
    );
  });

  /**
   * Removes products from the bundle using the Bundle API.
   *
   * This wraps the organization-nested Bundle remove-products route and shows a toast on success.
   */
  readonly removeProductsFromBundle = this.effect(
    (products$: Observable<BundleProductsUpdateProduct[]> | BundleProductsUpdateProduct[]) => {
      const o = Array.isArray(products$) ? of(products$) : products$;
      return o.pipe(
        withLatestFrom(this.item$, this.bundleProducts$, this.store.select(selectSelectedOrganizationId)),
        exhaustMap(([products, item, previousBundleProducts, organizationId]) => {
          let request$ = EMPTY as Observable<never>;
          const hasValidItemId = item?.id !== undefined && item?.id !== null;
          const hasProducts = products.length > 0;
          if (hasValidItemId && hasProducts) {
            this.addLoading('bundleProducts');
            const body: BundleProductsUpdateRequest = {
              products,
            };
            request$ = this.bundleApi.removeProductsFromBundle(item.id as number, body, organizationId!).pipe(
              switchMap(updated =>
                this.enrichIncludedProductsWithMergedPrices(
                  updated,
                  this.mapIncludedProductsToProducts(updated, previousBundleProducts)
                ).pipe(
                  switchMap(bundleProducts => {
                    this.patchState({bundle: updated, item: bundleResponseToItem(updated), bundleProducts});
                    this.toast.done('Products removed from bundle');
                    this.trackBundleProductChange(AnalyticsEvents.RemoveBundleProduct, updated.id, products);
                    return this.deactivateBundleIfIneligible(updated, bundleProducts, organizationId!);
                  })
                )
              ),
              tapResponse({
                next: updated => {
                  if (updated) {
                    this.patchState({bundle: updated, item: bundleResponseToItem(updated)});
                  }
                },
                error: () => {
                  this.setError('An error occurred. Please refresh and try again');
                },
                finalize: () => {
                  this.removeLoading('bundleProducts');
                },
              })
            ) as unknown as Observable<never>;
          }
          return request$;
        })
      );
    }
  );

  /**
   * Combined effect that removes products first, then adds products, then optionally updates
   * the bundle price — all sequentially. State is patched once the remove/add/price chain
   * completes (not after each individual call), so concurrent API responses from separate
   * effects cannot overwrite each other. A follow-up deactivation may patch state again if
   * the bundle becomes ineligible to stay active.
   */
  readonly updateBundleProducts = this.effect(
    (
      params$: Observable<{
        productsToAdd: BundleProductsUpdateProduct[];
        productsToRemove: BundleProductsUpdateProduct[];
        price?: number;
      }>
    ) => {
      return params$.pipe(
        withLatestFrom(this.bundle$, this.bundleProducts$, this.store.select(selectSelectedOrganizationId)),
        exhaustMap(([{productsToAdd, productsToRemove, price}, bundle, previousBundleProducts, organizationId]) => {
          if (!bundle || bundle.id === null || bundle.id === undefined) {
            return EMPTY;
          }

          this.addLoading('bundleProducts');

          const bundleId = bundle.id;

          // Build a chain of sequential operations.
          // Start with the current bundle as the baseline.
          let chain$: Observable<Bundle> = of(bundle);

          if (productsToRemove.length > 0) {
            const removeBody: BundleProductsUpdateRequest = {products: productsToRemove};
            chain$ = chain$.pipe(
              switchMap(() => this.bundleApi.removeProductsFromBundle(bundleId, removeBody, organizationId!))
            );
          }

          if (productsToAdd.length > 0) {
            const addBody: BundleProductsUpdateRequest = {products: productsToAdd};
            chain$ = chain$.pipe(
              switchMap(() => this.bundleApi.addProductsToBundle(bundleId, addBody, organizationId!))
            );
          }

          if (typeof price === 'number' && Number.isFinite(price)) {
            const updateRequest: UpdateBundleRequest = {price_cents: Math.round(price * 100)};
            chain$ = chain$.pipe(
              switchMap(() => this.bundleApi.updateBundle(bundleId, updateRequest, organizationId!))
            );
          }

          return chain$.pipe(
            switchMap(updated =>
              this.enrichIncludedProductsWithMergedPrices(
                updated,
                this.mapIncludedProductsToProducts(updated, previousBundleProducts)
              ).pipe(
                switchMap(bundleProducts => {
                  this.patchState({bundle: updated, item: bundleResponseToItem(updated), bundleProducts});
                  this.toast.done('Bundle updated');
                  this.trackBundleProductChange(AnalyticsEvents.RemoveBundleProduct, updated.id, productsToRemove);
                  this.trackBundleProductChange(AnalyticsEvents.AddBundleProduct, updated.id, productsToAdd);
                  return this.deactivateBundleIfIneligible(updated, bundleProducts, organizationId!);
                })
              )
            ),
            tapResponse({
              next: updated => {
                if (updated) {
                  this.patchState({bundle: updated, item: bundleResponseToItem(updated)});
                }
              },
              error: () => {
                this.setError('An error occurred. Please refresh and try again');
              },
              finalize: () => {
                this.removeLoading('bundleProducts');
              },
            })
          );
        })
      );
    }
  );

  readonly loadStore = this.effect<void>(trigger$ =>
    trigger$.pipe(
      tap(() => this.addLoading('store')),
      withLatestFrom(this.store.select(selectSelectedOrganizationId)),
      switchMap(([, orgId]) =>
        this.storeApi.get(orgId!).pipe(
          tapResponse({
            next: store => {
              this.patchState({store});
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.removeLoading('store');
            },
          })
        )
      )
    )
  );

  readonly setAllProductsToActive = this.effect($trigger => {
    return $trigger.pipe(
      withLatestFrom(this.bundle$),
      exhaustMap(([, bundle]) => {
        if (!bundle) return of();
        const products$ = bundle.products
          .filter(p => !p.active && hasActiveVariations(p))
          .map(p => ({id: p.id, active: true}))
          .map(p => this.productApi.update(p));

        this.addLoading('setAllProductsToActive');

        return forkJoin(products$).pipe(
          tapResponse({
            next: () => {
              this.loadBundle();
            },
            error: () => {
              this.setError('An error occurred. Please refresh and try again');
            },
            finalize: () => {
              this.removeLoading('setAllProductsToActive');
            },
          })
        );
      })
    );
  });

  /**
   * Fetches standalone catalog prices for ALL included products and sets `price` to the
   * standalone price so productPriceTotal always shows what customers would pay buying items
   * separately. For products whose bundle response omits variation data, `bundledPrice` is also
   * set from the standalone price; products that already have an embedded variation price
   * keep their `bundledPrice` (the bundle-allocated display price) untouched.
   */
  private enrichIncludedProductsWithMergedPrices(bundle: Bundle, mapped: Product[]): Observable<Product[]> {
    const raw = this.getDisplayableIncludedProductRows(bundle);
    const indices: number[] = [];
    const ids: string[] = [];
    const hasEmbeddedPrice: boolean[] = [];

    raw.forEach((p, index) => {
      const id = p?.id;
      if (id === undefined || id === null) {
        return;
      }
      const firstVariation = Array.isArray(p.variations) && p.variations.length > 0 ? p.variations[0] : undefined;
      const fromEmbedded = this.getVariationPriceDollars(firstVariation);
      indices.push(index);
      ids.push(String(id));
      hasEmbeddedPrice.push(!Number.isNaN(fromEmbedded));
    });

    if (ids.length === 0) {
      return of(mapped);
    }

    return forkJoin(
      ids.map(id =>
        this.productApi.getWithMergedVariations(id).pipe(catchError(() => of(undefined as Product | undefined)))
      )
    ).pipe(
      map(mergedItems => {
        const next = [...mapped];
        indices.forEach((rowIndex, i) => {
          const item = mergedItems[i];
          const prev = next[rowIndex];
          if (!prev || item === undefined) {
            return;
          }
          const standalonePrice = getProductDetailDisplayPrice(item);
          const previous = prev;

          if (hasEmbeddedPrice[i]) {
            // Product has an embedded variation price (bundle-allocated display price).
            // Only update the standalone `price`; keep `bundledPrice` as is.
            if (previous.price === standalonePrice) {
              return;
            }
            next[rowIndex] = {...prev, price: standalonePrice};
          } else {
            // No embedded variation — set both the standalone `price` and the current
            // bundle-context `bundledPrice` from the fetched standalone value.
            next[rowIndex] = {
              ...prev,
              price: previous.price ?? standalonePrice,
              bundledPrice: standalonePrice,
            };
          }
        });
        return next;
      })
    );
  }

  /**
   * Maps included products from the bundle API response into Product shape used by the UI.
   *
   * bundledPrice — per-row display price from the current API response (may change on bundle
   *                price updates).
   * price — standalone list price preserved from the previous mapping so that
   *         productPriceTotal stays stable across bundle price changes. On first load
   *         (no previous) it falls back to the variation price.
   */
  private mapIncludedProductsToProducts(bundle: Bundle, previousProducts: Product[] = []): Product[] {
    const rawProducts = this.getDisplayableIncludedProductRows(bundle);
    const previousById = this.buildPreviousProductMap(previousProducts);

    return rawProducts.map(item => {
      const id = item.id !== undefined && item.id !== null ? String(item.id) : undefined;
      const firstVariation =
        Array.isArray(item.variations) && item.variations.length > 0 ? item.variations[0] : undefined;
      const bundledPrice = this.getVariationPriceDollars(firstVariation);

      const images = this.mapIncludedProductImages(item.images, item.name);

      const previous = id ? previousById.get(id) : undefined;
      const active = this.getIncludedProductActiveState(item, firstVariation, previous);
      const effectivePrice = Number.isNaN(bundledPrice) ? undefined : bundledPrice;
      const onlySoldInBundles = item.only_sold_in_bundles ?? previous?.only_sold_in_bundles ?? false;

      if (
        previous &&
        this.isIncludedProductUnchanged(
          previous,
          item.name,
          item.description,
          active,
          effectivePrice,
          images,
          item.display_metadata?.brand,
          onlySoldInBundles
        )
      ) {
        return previous;
      }

      return this.buildIncludedProduct(id, item, active, images, bundledPrice, previous);
    });
  }

  /** Builds a lookup map of previous products keyed by ID. */
  private buildPreviousProductMap(previousProducts: Product[]): Map<string, Product> {
    return new Map(
      previousProducts
        .filter(product => getProductId(product) !== undefined)
        .map(product => [String(getProductId(product)), product] as const)
    );
  }

  /**
   * Constructs a Product for an included bundle row, preserving the standalone list price
   * from a previous mapping when available.
   */
  private buildIncludedProduct(
    id: string | undefined,
    item: any,
    active: boolean,
    images: Array<{src: string; alt: string}>,
    variationPriceDollars: number,
    previous: Product | undefined
  ): Product {
    const previousRow: Partial<Product> = previous ?? {};
    const standalonePrice = this.extractPreviousListPrice(previous) ?? this.toDefinedPrice(variationPriceDollars);
    const bundledPrice = this.toDefinedPrice(variationPriceDollars);
    const displayMetadata = {
      ...(item.display_metadata ?? {}),
      ...(images.length > 0 ? {images} : {}),
    };

    return {
      id: id ? Number(id) : undefined,
      name: item.name,
      description: item.description ?? undefined,
      active,
      only_sold_in_bundles: item.only_sold_in_bundles ?? previous?.only_sold_in_bundles ?? false,
      price: standalonePrice ?? previousRow.price,
      bundledPrice,
      display_metadata: Object.keys(displayMetadata).length > 0 ? displayMetadata : undefined,
    };
  }

  /** Extracts a valid standalone price from a previous product row, or undefined. */
  private extractPreviousListPrice(previous: Product | undefined): number | undefined {
    const previousPrice = previous?.price;
    return typeof previousPrice === 'number' && !Number.isNaN(previousPrice) ? previousPrice : undefined;
  }

  /**
   * Extracts the dollar price from an embedded variation (prefers price_cents).
   * Used as the per-row display price (`bundledPrice`) and, on first load, as the
   * initial standalone `price`.
   */
  private getVariationPriceDollars(firstVariation: any): number {
    let result = NaN;
    if (firstVariation) {
      const cents = firstVariation.price_cents;
      if (typeof cents === 'number' && !Number.isNaN(cents)) {
        result = cents / 100;
      } else {
        const raw = firstVariation.price;
        if (raw !== undefined && raw !== null) {
          const n = typeof raw === 'number' ? raw : Number(String(raw));
          result = Number.isNaN(n) ? NaN : n;
        }
      }
    }
    return result;
  }

  private toDefinedPrice(value: number): number | undefined {
    return Number.isNaN(value) ? undefined : value;
  }

  private getIncludedProductActiveState(product: any, firstVariation: any, previous?: Product): boolean {
    if (typeof product.active === 'boolean') {
      return product.active;
    }
    if (typeof product.visible === 'boolean') {
      return product.visible;
    }
    if (typeof firstVariation?.active === 'boolean') {
      return firstVariation.active;
    }
    if (typeof firstVariation?.visible === 'boolean') {
      return firstVariation.visible;
    }
    if (typeof previous?.active === 'boolean') {
      return previous.active;
    }
    return false;
  }

  private mapIncludedProductImages(images: unknown, fallbackAlt: string): Array<{src: string; alt: string}> {
    if (!Array.isArray(images) || images.length === 0) {
      return [];
    }
    return images
      .map((image: any) => ({
        src: image?.src ?? '',
        alt: image?.alt ?? fallbackAlt,
      }))
      .filter(image => image.src !== '');
  }

  private isIncludedProductUnchanged(
    previous: Product,
    name: string,
    description: string | null | undefined,
    active: boolean,
    price: number | undefined,
    images: Array<{src: string; alt: string}>,
    brand: string | undefined,
    onlySoldInBundles: boolean = previous.only_sold_in_bundles ?? false
  ): boolean {
    const nameChanged = previous.name !== name;
    const descriptionChanged = previous.description !== (description ?? undefined);
    const activeChanged = previous.active !== active;
    const priceChanged = this.hasPriceChanged(previous, price);
    const onlySoldInBundlesChanged = (previous.only_sold_in_bundles ?? false) !== onlySoldInBundles;
    const imagesChanged = !this.areImagesEqual(previous.display_metadata?.images, images);
    const previousBrand =
      typeof previous.display_metadata?.brand === 'string' ? previous.display_metadata.brand : undefined;
    const brandChanged = previousBrand !== brand;

    return !(
      nameChanged ||
      descriptionChanged ||
      activeChanged ||
      priceChanged ||
      onlySoldInBundlesChanged ||
      imagesChanged ||
      brandChanged
    );
  }

  /** Compares the current row price (`bundledPrice`, falling back to `price`) against an incoming price. */
  private hasPriceChanged(previous: Product | undefined, price: number | undefined): boolean {
    const previousDisplayPrice =
      typeof previous?.bundledPrice === 'number' && !Number.isNaN(previous.bundledPrice)
        ? previous.bundledPrice
        : typeof previous?.price === 'number' && !Number.isNaN(previous.price)
          ? previous.price
          : undefined;
    return previousDisplayPrice !== price;
  }

  /** Deep-compares two image arrays by src and alt. */
  private areImagesEqual(
    previousImages: Array<{src: string; alt: string}> | undefined,
    images: Array<{src: string; alt: string}>
  ): boolean {
    const resolvedPreviousImages = Array.isArray(previousImages) ? previousImages : [];
    if (resolvedPreviousImages.length !== images.length) {
      return false;
    }
    return images.every(
      (img, i) => resolvedPreviousImages[i]?.src === img.src && resolvedPreviousImages[i]?.alt === img.alt
    );
  }

  private getDisplayableIncludedProductRows(bundle: Bundle): any[] {
    return Array.isArray(bundle.products)
      ? (bundle.products as any[]).filter(product => !this.isDiscardedIncludedProduct(product))
      : [];
  }

  private removeDiscardedProductsFromBundle(
    bundleId: number,
    bundle: Bundle | undefined,
    organizationId: string
  ): Observable<Bundle | null> {
    const products = Array.isArray(bundle?.products) ? (bundle!.products as any[]) : [];
    const discardedProducts = products
      .filter(product => this.isDiscardedIncludedProduct(product))
      .map(product => Number(product?.id))
      .filter(id => Number.isFinite(id))
      .map(productId => ({product_id: productId}));

    if (discardedProducts.length === 0) {
      return of(null);
    }

    return this.bundleApi.removeProductsFromBundle(bundleId, {products: discardedProducts}, organizationId);
  }

  private isDiscardedIncludedProduct(product: any): boolean {
    return (
      product?.disabled === true ||
      product?.item?.disabled === true ||
      (product?.disabled_at !== undefined && product?.disabled_at !== null) ||
      (product?.item?.disabled_at !== undefined && product?.item?.disabled_at !== null)
    );
  }

  private deactivateBundleIfIneligible(
    bundle: Bundle,
    bundleProducts: Product[],
    organizationId: string
  ): Observable<Bundle | null> {
    if (!bundle.active || canBundleBeActive(bundle.name, bundleProducts)) {
      return of(null);
    }

    const body = itemToUpdateBundleRequest(bundleResponseToItem(bundle), {active: false});
    return this.bundleApi.updateBundle(bundle.id, body, organizationId);
  }

  private trackBundleStatusChange(previousActive: boolean | undefined, updated: Bundle): void {
    if (typeof updated.active !== 'boolean' || previousActive === updated.active) {
      return;
    }

    this.analytics.trackAction(
      updated.active ? AnalyticsEvents.ActivateBundle : AnalyticsEvents.DeactivateBundle,
      this.buildBundleAnalyticsProperties(updated.id)
    );
  }

  private trackBundleProductChange(
    action: typeof AnalyticsEvents.AddBundleProduct | typeof AnalyticsEvents.RemoveBundleProduct,
    bundleId: Bundle['id'] | undefined | null,
    products: BundleProductsUpdateProduct[]
  ): void {
    if (products.length === 0) {
      return;
    }

    this.analytics.trackAction(
      action,
      this.buildBundleAnalyticsProperties(bundleId, {
        product_count: products.length,
      })
    );
  }

  private trackBundleMedia(bundleId: Bundle['id'] | undefined | null): void {
    this.analytics.trackAction(AnalyticsEvents.AddBundleMedia, this.buildBundleAnalyticsProperties(bundleId));
  }

  private buildBundleAnalyticsProperties(
    bundleId: Bundle['id'] | undefined | null,
    properties: AnalyticsEventProperties = {}
  ): AnalyticsEventProperties {
    return {
      ...properties,
      ...(bundleId !== undefined && bundleId !== null ? {bundle_id: String(bundleId)} : {}),
      event_type: 1,
    };
  }

  private hasNewBundleMedia(previousImages: Image[] | undefined, nextImages: Image[]): boolean {
    const previousSources = new Set((previousImages ?? []).map(image => image.src).filter(Boolean));
    return nextImages.some(image => !!image.src && !previousSources.has(image.src));
  }

  private updateBundleCatalogs(
    bundleId: number,
    selectedCatalogIds: number[],
    catalogs: CatalogSummary[],
    organizationCatalogs: CatalogSummary[]
  ): Observable<{catalogs: CatalogSummary[]; changed: boolean}> {
    return this.catalogMembershipSync
      .sync({
        catalogable: {type: 'bundle', id: bundleId},
        currentCatalogIds: catalogs.map(catalog => catalog.id),
        selectedCatalogIds,
      })
      .pipe(
        map(result => {
          const catalogsById = new Map([...catalogs, ...organizationCatalogs].map(catalog => [catalog.id, catalog]));
          return {
            catalogs: result.selectedCatalogIds
              .map(catalogId => catalogsById.get(catalogId))
              .filter((catalog): catalog is CatalogSummary => catalog !== undefined),
            changed: result.catalogIdsToAssign.length > 0 || result.catalogIdsToRemove.length > 0,
          };
        })
      );
  }

  private extractQueryParams(
    params: Partial<Record<keyof ProductQueryParams, unknown>> | undefined
  ): Record<string, string> {
    const defaults = defaultProductQueryParams();
    const merged = {...defaults, ...params};
    const allowedKeys: (keyof ProductQueryParams)[] = ['page', 'take', 'query', 'sortBy', 'direction'];
    return allowedKeys.reduce<Record<string, string>>((acc, key) => {
      const value = merged[key];
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {});
  }

  constructor(
    private bundleApi: BundleApiService,
    private productApi: ProductApiService,
    private store: Store,
    private toast: SeFeToastService,
    private storeApi: StoreApiService,
    private catalogApi: CatalogApiService,
    private catalogMembershipSync: CatalogMembershipSyncService,
    private analytics: AnalyticsService
  ) {
    super({
      item: undefined,
      priceSchedule: undefined,
      loadingArray: [],
      store: undefined,
      error: null,
      bundleProducts: [],
      bundle: undefined,
      catalogs: [],
      catalogsLoading: false,
      organizationCatalogs: [],
      organizationCatalogsLoading: false,
    });
  }
}
